import { catchError, concatAll, filter, map, mergeMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import i18next from 'i18next';
import { PayloadAction } from '@reduxjs/toolkit';
import { showNotification, showNotificationFromError } from 'components/app/store/UserSlice';
import RootEpic from 'store/RootEpic';
import {
  ListMsEntraIDGroupTargetAccessOptions,
  createMsEntraIDGroupTargetAccess,
  deleteMsEntraIDGroupTargetAccess,
  getMsEntraIDGroupTargetAccess,
  listMsEntraIDGroupTargetAccess,
  updateMsEntraIDGroupTargetAccess,
} from 'gql/MsEntraIDGroupTargetAccess';
import { PermissionAction, PermissionResource, hasPermission } from 'gql/Permission';
import MsEntraIDGroupTargetAccessSlice from './MsEntraIDGroupTargetAccessSlice';

// GET
export const getMsEntraIDGroupTargetAccessEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDGroupTargetAccessSlice.actions.getMsEntraIDGroupTargetAccessByID.match),
    mergeMap(({ payload }) => {
      return from(getMsEntraIDGroupTargetAccess(payload)).pipe(
        map((response) => MsEntraIDGroupTargetAccessSlice.actions.getMsEntraIDGroupTargetAccessByIDSucceeded(response)),
        catchError((error) => {
          return of(MsEntraIDGroupTargetAccessSlice.actions.getMsEntraIDGroupTargetAccessByIDFailed(error));
        })
      );
    })
  );

export const getMsEntraIDGroupTargetAccessFailedEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDGroupTargetAccessSlice.actions.getMsEntraIDGroupTargetAccessByIDFailed.match),
    map(({ payload }) =>
      showNotificationFromError({ msg: i18next.t('FAILED_GET_MS_ENTRA_ID_GROUP_TARGET_ACCESS'), err: payload })
    )
  );

// LIST
export const listMsEntraIDGroupTargetAccessEpic: RootEpic = (action$, state$) => {
  return action$.pipe(
    filter(() =>
      hasPermission(
        state$.value.user.user?.permissions,
        PermissionAction.READ,
        PermissionResource.MS_ENTRA_ID_GROUP_TARGET_ACCESS
      )
    ),
    filter(
      (action): action is PayloadAction<ListMsEntraIDGroupTargetAccessOptions> =>
        MsEntraIDGroupTargetAccessSlice.actions.listMsEntraIDGroupTargetAccessForTargetOrGroup.match(action) ||
        MsEntraIDGroupTargetAccessSlice.actions.listNextPageMsEntraIDGroupTargetAccessForTargetOrGroup.match(action)
    ),
    mergeMap(({ payload }) => {
      return from(listMsEntraIDGroupTargetAccess(payload)).pipe(
        map((response) => {
          return response.nextToken
            ? [
                MsEntraIDGroupTargetAccessSlice.actions.listMsEntraIDGroupTargetAccessForTargetOrGroupSucceeded(
                  response.items
                ),
                MsEntraIDGroupTargetAccessSlice.actions.listNextPageMsEntraIDGroupTargetAccessForTargetOrGroup({
                  includeAllTargetGroups: payload.includeAllTargetGroups,
                  targetOrTargetGroupID: payload.targetOrTargetGroupID,
                  userADGroups: payload.userADGroups,
                  nextToken: response.nextToken,
                  size: payload.size,
                }),
              ]
            : [
                MsEntraIDGroupTargetAccessSlice.actions.listMsEntraIDGroupTargetAccessForTargetOrGroupSucceeded(
                  response.items
                ),
              ];
        }),
        concatAll(),
        catchError((error) => {
          return of(
            MsEntraIDGroupTargetAccessSlice.actions.listMsEntraIDGroupTargetAccessForTargetOrGroupFailed(error)
          );
        })
      );
    })
  );
};

export const listMsEntraIDGroupTargetAccessFailedEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDGroupTargetAccessSlice.actions.listMsEntraIDGroupTargetAccessForTargetOrGroupFailed.match),
    map(({ payload }) =>
      showNotificationFromError({ msg: i18next.t('FAILED_GET_MS_ENTRA_ID_GROUP_TARGET_ACCESS'), err: payload })
    )
  );

// CREATE
export const createMsEntraIDGroupTargetAccessEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDGroupTargetAccessSlice.actions.createMsEntraIDGroupTargetAccess.match),
    mergeMap(({ payload }) =>
      from(createMsEntraIDGroupTargetAccess(payload)).pipe(
        map((response) => MsEntraIDGroupTargetAccessSlice.actions.createMsEntraIDGroupTargetAccessSucceeded(response)),
        catchError((error) => of(MsEntraIDGroupTargetAccessSlice.actions.createMsEntraIDGroupTargetAccessFailed(error)))
      )
    )
  );

export const createMsEntraIDGroupTargetAccessSuccessEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDGroupTargetAccessSlice.actions.createMsEntraIDGroupTargetAccessSucceeded.match),
    mergeMap((action) => {
      return [
        showNotification({
          type: 'success',
          contents: i18next.t('SUCCESS_CREATED_MS_ENTRA_ID_GROUP_TARGET_ACCESS', action.payload.id),
        }),
      ];
    })
  );

export const createMsEntraIDGroupTargetAccessFailedEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDGroupTargetAccessSlice.actions.createMsEntraIDGroupTargetAccessFailed.match),
    map(({ payload }) =>
      showNotificationFromError({ msg: i18next.t('FAILED_CREATE_MS_ENTRA_ID_GROUP_TARGET_ACCESS'), err: payload })
    )
  );

// UPDATE
export const updateMsEntraIDGroupTargetAccessEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDGroupTargetAccessSlice.actions.updateMsEntraIDGroupTargetAccess.match),
    mergeMap(({ payload }) => {
      return from(updateMsEntraIDGroupTargetAccess(payload)).pipe(
        map((response) => MsEntraIDGroupTargetAccessSlice.actions.updateMsEntraIDGroupTargetAccessSucceeded(response)),
        catchError((error) => of(MsEntraIDGroupTargetAccessSlice.actions.updateMsEntraIDGroupTargetAccessFailed(error)))
      );
    })
  );

export const updateMsEntraIDGroupTargetAccessSuccessEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDGroupTargetAccessSlice.actions.updateMsEntraIDGroupTargetAccessSucceeded.match),
    mergeMap((action) => {
      return [
        showNotification({
          type: 'success',
          contents: i18next.t('SUCCESS_UPDATED_MS_ENTRA_ID_GROUP_TARGET_ACCESS', action.payload.id),
        }),
      ];
    })
  );

export const updateMsEntraIDGroupTargetAccessFailedEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDGroupTargetAccessSlice.actions.updateMsEntraIDGroupTargetAccessFailed.match),
    map(({ payload }) => {
      return showNotificationFromError({
        msg: i18next.t('FAILED_UPDATE_MS_ENTRA_ID_GROUP_TARGET_ACCESS'),
        err: payload,
      });
    })
  );

// DELETE
export const deleteMsEntraIDGroupTargetAccessEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDGroupTargetAccessSlice.actions.deleteMsEntraIDGroupTargetAccess.match),
    mergeMap(({ payload }) => {
      return from(deleteMsEntraIDGroupTargetAccess(payload)).pipe(
        map((response) => MsEntraIDGroupTargetAccessSlice.actions.deleteMsEntraIDGroupTargetAccessSucceeded(response)),
        catchError((error) => of(MsEntraIDGroupTargetAccessSlice.actions.deleteMsEntraIDGroupTargetAccessFailed(error)))
      );
    })
  );

export const deleteMsEntraIDGroupTargetAccessSuccessEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDGroupTargetAccessSlice.actions.deleteMsEntraIDGroupTargetAccessSucceeded.match),
    mergeMap((action) => {
      return [
        showNotification({
          type: 'success',
          contents: i18next.t('SUCCESS_DELETED_MS_ENTRA_ID_GROUP_TARGET_ACCESS', action.payload.id),
        }),
      ];
    })
  );

export const deleteMsEntraIDGroupTargetAccessFailedEpic: RootEpic = (action$) =>
  action$.pipe(
    filter(MsEntraIDGroupTargetAccessSlice.actions.deleteMsEntraIDGroupTargetAccessFailed.match),
    map(({ payload }) => {
      return showNotificationFromError({
        msg: i18next.t('FAILED_DELETE_MS_ENTRA_ID_GROUP_TARGET_ACCESS'),
        err: payload,
      });
    })
  );

const MsEntraIDGroupTargetAccessEpics: RootEpic[] = [
  getMsEntraIDGroupTargetAccessEpic,
  getMsEntraIDGroupTargetAccessFailedEpic,
  listMsEntraIDGroupTargetAccessEpic,
  listMsEntraIDGroupTargetAccessFailedEpic,
  createMsEntraIDGroupTargetAccessEpic,
  createMsEntraIDGroupTargetAccessSuccessEpic,
  createMsEntraIDGroupTargetAccessFailedEpic,
  updateMsEntraIDGroupTargetAccessEpic,
  updateMsEntraIDGroupTargetAccessSuccessEpic,
  updateMsEntraIDGroupTargetAccessFailedEpic,
  deleteMsEntraIDGroupTargetAccessEpic,
  deleteMsEntraIDGroupTargetAccessFailedEpic,
  deleteMsEntraIDGroupTargetAccessSuccessEpic,
];

export default MsEntraIDGroupTargetAccessEpics;
